<template>
  <div class="EventsRelatedContentblock">
    <div class="grid">
      <div class="w-full flex flex-nowrap align-end justify-space-between mb-xxlg">
        <div class="col-8-12 sm-col-1-1">
          <p
            v-if="content.subtitle"
            class="subtitle mb-xlg"
          >
            {{ content.subtitle }}
          </p>
          <UIHeadingType
            :heading-type="content.seoHeadingtype"
            :title="content.title"
          />
        </div>
        <div class="sm-hide col-4-12 sm-col-1-1 flex justify-end">
          <UIContentblockButton
            class="btn btn-tertiary"
            :name="content.linkObject?.name"
            :link="content.linkObject?.link"
            :target="content.linkObject?.target"
          />
        </div>
      </div>

      <div class="w-full">
        <div
          v-if="events.length > 0"
          class="events-grid flex flex-wrap"
        >
          <div
            v-for="(event, idx) in events"
            :key="`${event.id}-${idx}`"
            class="col-1-3 sm-col-1-1"
          >
            <UIRelatedArticle
              :title="event.title"
              :subtitle="event.subTitle"
              :url="event.url"
              :image-object="event.image1"
              :label="event.genres?.[0]?.name"
              :date="getEventItemDate(event)"
            />
          </div>
        </div>

        <div
          v-else
          class="col-1-1"
        >
          <p>Geen resultaten gevonden</p>
        </div>

        <div
          v-if="content.linkObject?.name"
          class="col-1-1 hide sm-show mt-xxxlg"
        >
          <UIContentblockButton
            class="btn btn-tertiary"
            :name="content.linkObject?.name"
            :link="content.linkObject?.link"
            :target="content.linkObject?.target"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { RelatedEventsContent } from '~/types/contentblocks'

  const props = defineProps({
    content: {
      type: Object as PropType<RelatedEventsContent>,
      required: true,
    },
    mainEventId: {
      type: [String, Number] as PropType<string | number>,
      required: false,
      default: null,
    },
    contentblockIdx: {
      type: [String, Number] as PropType<string | number>,
      required: false,
      default: null,
    },
  })

  const { getEventItemDate } = useEventDate()

  const filterOptions = await getEventFilters()

  const initialFilters = computed(() => {
    const filters: Record<string, any> = {}

    filters.genre = props.content.genreList?.map((c) => c._id) ?? []
    filters.location = props.content.locationList?.map((l) => l._id) ?? []
    filters.eventAttribute = props.content.labelList?.map((l) => l._id) ?? []

    return filters
  })

  const { fetchEvents, events, setPageSize, setItemsExcluded, setItemsIncluded, setExcludedFilters } = useEventOverview(
    filterOptions,
    {
      initialActiveFilters: initialFilters.value,
      instanceKey: props.contentblockIdx,
    },
  )

  const excludedFilters = computed(() => {
    const excluded = {}
    if (props.content.excludedLabelList != null && props.content.excludedLabelList.length > 0) {
      excluded.eventAttribute = props.content.excludedLabelList?.map((l) => l._id) ?? []
    }

    if (props.content.excludedLocationList != null && props.content.excludedLocationList.length > 0) {
      excluded.location = props.content.excludedLocationList?.map((l) => l._id) ?? []
    }

    if (props.content.excludedGenreList != null && props.content.excludedGenreList.length > 0) {
      excluded.genre = props.content.excludedGenreList?.map((l) => l._id) ?? []
    }

    if (props.content.excludeProgramLabelList != null && props.content.excludeProgramLabelList.length > 0) {
      excluded.programAttribute = props.content.excludeProgramLabelList?.map((p) => p._id) ?? []
    }

    return excluded
  })

  setExcludedFilters(excludedFilters.value)

  setPageSize(props.content.maxArticles || 12)

  if (props.content.eventList && props.content.eventList != null) {
    const ids = props.content.eventList.map((event) => event._id)
    setItemsIncluded(ids)
  } else {
    if (props.mainEventId) {
      setItemsExcluded([props.mainEventId])
    }
  }

  await fetchEvents()
</script>

<style lang="scss" scoped>
  .events-grid {
    row-gap: var(--margin-xlg);
  }
</style>
